// src/components/Account/AccountDashboard.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Fade,
  Avatar,
  useTheme,
  alpha,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Stack,
  Tooltip,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  LinearProgress,
  Badge,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  AttachMoney as MoneyIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  CardGiftcard as ComplimentaryIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Restaurant as RestaurantIcon,
  ShoppingCart as CartIcon,
  Storefront as StoreIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Remove as RemoveIcon,
  Dashboard as DashboardIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningAmberIcon,
  ErrorOutline as ErrorIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
  Cancel as CancelIcon,
  Paid as PaidIcon,
  Discount as DiscountIcon,
  Search as SearchIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';

// Import local components
import AccountHeader from '../../components/Account/Header';
import AccountSidebar from '../../components/Account/Sidebar';
import AccountFooter from '../../components/Account/Footer';
import Preloader from '../../theme/Preloader';

// Import chart library
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  Filler
);

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year', label: 'This Year' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom Range' },
];

const PAYMENT_COLORS = {
  'Cash': '#4CAF50',
  'Card': '#1976D2',
  'Bank Transfer': '#9C27B0',
  'Mobile Money': '#FF6F00',
  'Complimentary': '#FF9800',
};

const LOCATION_COLORS = {
  3: { primary: '#1565C0', light: '#E3F2FD', label: 'Continental' },
  4: { primary: '#6A1B9A', light: '#F3E5F5', label: 'Lounge' },
  5: { primary: '#2E7D32', light: '#E8F5E9', label: 'African' },
};

const CHART_COLORS = [
  '#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336',
  '#00BCD4', '#8BC34A', '#FF5722', '#3F51B5', '#E91E63'
];

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatCurrency = (amount, currency = '₦') => {
  if (amount === null || amount === undefined) return `${currency} 0.00`;
  return `${currency} ${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const getLocationColor = (locationId) => {
  return LOCATION_COLORS[locationId] || { primary: '#757575', light: '#F5F5F5', label: 'Unknown' };
};

// ============================================
// TOKEN & AUTH FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    console.error('[Account] Error checking token expiry:', err);
    return true;
  }
};

const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.warn('[Account] No refresh token available');
      throw new Error('No refresh token available');
    }

    console.debug('[Account] Refreshing auth token...');
    const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error(`Refresh failed with status: ${response.status}`);
    }

    const data = await response.json();
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      console.debug('[Account] Token refreshed successfully');
      return data.access_token;
    }
    throw new Error('No access token in refresh response');
  } catch (err) {
    console.error('[Account] Token refresh failed:', err.message);
    localStorage.clear();
    window.location.href = '/restaurantAuth';
    return null;
  }
};

// ============================================
// API HELPERS
// ============================================

const apiRequest = async (endpoint, options = {}) => {
  let token = localStorage.getItem('access_token');
  
  if (!token || isTokenExpired(token)) {
    console.debug('[Account] Token invalid, attempting refresh...');
    token = await refreshAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  let url = `${FULL_API_URL}${endpoint}`;
  
  if (options.params) {
    const params = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  console.debug(`[Account] API Request: ${options.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, { 
      ...options, 
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined
    });

    if (response.status === 401) {
      console.debug('[Account] Received 401, attempting token refresh...');
      const newToken = await refreshAuthToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (retryResponse.ok) {
          const contentType = retryResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            return await retryResponse.json();
          }
          return { message: await retryResponse.text() };
        }
        if (retryResponse.status === 401) {
          throw new Error('Authentication failed after refresh');
        }
      }
      throw new Error('Authentication failed');
    }

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { message: text || 'Non-JSON response received' };
    }

    if (!response.ok) {
      const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (err) {
    console.error('[Account] API Error:', err.message);
    throw err;
  }
};

// ============================================
// STATISTICS CARD COMPONENT
// ============================================

const StatCard = React.memo(({ title, value, subtitle, icon, color, trend, trendValue, loading, onClick }) => {
  const theme = useTheme();
  
  if (loading) {
    return (
      <Card elevation={0} sx={{ borderRadius: '16px', height: '100%' }}>
        <CardContent>
          <Skeleton variant="rectangular" height={60} />
          <Skeleton variant="text" width="60%" />
        </CardContent>
      </Card>
    );
  }
  
  const TrendIcon = trend === 'up' ? ArrowUpIcon : trend === 'down' ? ArrowDownIcon : RemoveIcon;
  const trendColor = trend === 'up' ? '#4CAF50' : trend === 'down' ? '#F44336' : '#FF9800';
  
  return (
    <Card 
      elevation={0} 
      sx={{ 
        borderRadius: '16px', 
        height: '100%',
        border: `1px solid ${alpha(color || '#1976D2', 0.12)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        } : {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        },
        position: 'relative',
        overflow: 'hidden',
      }}
      onClick={onClick}
    >
      <Box 
        sx={{ 
          position: 'absolute', 
          top: 0, 
          right: 0, 
          width: '100px', 
          height: '100px',
          borderRadius: '0 16px 0 100px',
          bgcolor: alpha(color || '#1976D2', 0.06),
          pointerEvents: 'none',
        }} 
      />
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
       
<Typography 
  variant="caption" 
  color="text.secondary" 
  fontWeight={700}
  sx={{ 
    fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },  // Reduced
    textTransform: 'uppercase', 
    letterSpacing: '0.5px',
    display: 'block',
  }}
>
  {title}
</Typography>
<Typography 
  variant="h5" 
  fontWeight={800}
  sx={{ 
    fontSize: { xs: '1.0rem', sm: '1.2rem', md: '1.4rem' },  // Reduced
    mt: 0.5,
    color: color || 'text.primary',
    wordBreak: 'break-word',
  }}
>
  {value}
</Typography>
            {subtitle && (
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' }, mt: 0.25, display: 'block' }}
              >
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <TrendIcon sx={{ fontSize: 16, color: trendColor }} />
                <Typography 
                  variant="caption" 
                  fontWeight={700}
                  sx={{ color: trendColor, fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.75rem' } }}
                >
                  {Math.abs(trendValue)}% from previous period
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar 
            sx={{ 
              bgcolor: alpha(color || '#1976D2', 0.12),
              color: color || '#1976D2',
              width: { xs: 44, sm: 52, md: 56 },
              height: { xs: 44, sm: 52, md: 56 },
              flexShrink: 0,
              ml: 1,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
});

StatCard.displayName = 'StatCard';

// ============================================
// MAIN ACCOUNT DASHBOARD COMPONENT
// ============================================

const AccountDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [period, setPeriod] = useState('today');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [activeLocationTab, setActiveLocationTab] = useState(0);
  
  // Location filter options
  const [locationOptions, setLocationOptions] = useState([]);
  
  // Transactions
  const [transactions, setTransactions] = useState([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionPageSize] = useState(10);
  
  // Location Summary Dialog
  const [locationSummaryDialogOpen, setLocationSummaryDialogOpen] = useState(false);
  const [locationSummary, setLocationSummary] = useState(null);
  
  // Date range picker
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // ============================================
  // REFS
  // ============================================
  
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  // ============================================
  // DATA FETCHING
  // ============================================
  
  const fetchDashboardData = useCallback(async () => {
    try {
      console.debug('[Account] Fetching dashboard data...');
      setIsFetching(true);
      
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      
      const params = { period };
      if (selectedLocation !== 'all') {
        params.location_id = parseInt(selectedLocation);
      }
      
      // Add custom date range if provided
      if (period === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }
      
      const data = await apiRequest('/account/dashboard', { params });
      console.debug('[Account] Dashboard data fetched:', data);
      
      if (isMountedRef.current) {
        setDashboardData(data);
        
        // Extract location options for tabs
        if (data.location_performance && data.location_performance.length > 0) {
          const options = data.location_performance.map(loc => ({
            id: loc.location_id,
            name: loc.location_name,
            revenue: loc.total_revenue,
            orders: loc.total_orders,
            growth: loc.growth_percentage,
          }));
          setLocationOptions(options);
        }
      }
      
      return data;
    } catch (err) {
      if (err.name === 'AbortError') {
        console.debug('[Account] Request aborted');
        return null;
      }
      console.error('[Account] Error fetching dashboard data:', err);
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message: `Failed to fetch dashboard: ${err.message}`,
          severity: 'error',
        });
      }
      return null;
    } finally {
      if (isMountedRef.current) {
        setIsFetching(false);
      }
    }
  }, [period, selectedLocation, customStartDate, customEndDate]);

  const fetchTransactions = useCallback(async () => {
    try {
      console.debug('[Account] Fetching transactions...');
      
      const params = {
        period,
        page: transactionPage,
        page_size: transactionPageSize,
      };
      
      if (selectedLocation !== 'all') {
        params.location_id = parseInt(selectedLocation);
      }
      
      if (period === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }
      
      const data = await apiRequest('/account/transactions', { params });
      console.debug('[Account] Transactions fetched:', data);
      
      if (isMountedRef.current) {
        setTransactions(data.transactions || []);
        setTransactionsTotal(data.total_count || 0);
      }
      
      return data;
    } catch (err) {
      console.error('[Account] Error fetching transactions:', err);
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message: `Failed to fetch transactions: ${err.message}`,
          severity: 'error',
        });
      }
      return null;
    }
  }, [period, transactionPage, transactionPageSize, selectedLocation, customStartDate, customEndDate]);

  const fetchLocationSummary = useCallback(async (locationId) => {
    try {
      console.debug(`[Account] Fetching location summary for ${locationId}...`);
      
      const params = {
        location_id: locationId,
        period: period,
      };
      
      if (period === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }
      
      const data = await apiRequest('/account/location-summary', { params });
      
      console.debug('[Account] Location summary fetched:', data);
      
      if (isMountedRef.current) {
        setLocationSummary(data);
        setLocationSummaryDialogOpen(true);
      }
      
      return data;
    } catch (err) {
      console.error('[Account] Error fetching location summary:', err);
      setSnackbar({
        open: true,
        message: `Failed to fetch location summary: ${err.message}`,
        severity: 'error',
      });
      return null;
    }
  }, [period, customStartDate, customEndDate]);

  const loadData = useCallback(async () => {
    console.debug('[Account] Loading data...');
    await Promise.all([
      fetchDashboardData(),
      fetchTransactions(),
    ]);
  }, [fetchDashboardData, fetchTransactions]);

  // ============================================
  // EFFECTS
  // ============================================
  
  // Initial load
  useEffect(() => {
    isMountedRef.current = true;
    
    console.debug('[Account] Initializing Account Dashboard...');
    
    // Set default custom dates
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setCustomStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setCustomEndDate(today.toISOString().split('T')[0]);
    
    loadData();
    
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }, 800);
    
    return () => {
      clearTimeout(timer);
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-load when period or location changes
  useEffect(() => {
    if (!isLoading) {
      console.debug(`[Account] Period or location changed: period=${period}, location=${selectedLocation}`);
      loadData();
    }
  }, [period, selectedLocation, loadData, isLoading]);

  // Auto-load when custom date changes (but only when period is custom)
  useEffect(() => {
    if (!isLoading && period === 'custom' && customStartDate && customEndDate) {
      console.debug('[Account] Custom date range changed, loading data...');
      loadData();
    }
  }, [customStartDate, customEndDate, period, loadData, isLoading]);

  // ============================================
  // HANDLERS
  // ============================================
  
  const handleRefresh = useCallback(async () => {
    console.debug('[Account] Manual refresh triggered');
    await loadData();
    setSnackbar({
      open: true,
      message: 'Data refreshed successfully!',
      severity: 'success',
    });
  }, [loadData]);

  const handlePeriodChange = useCallback((event) => {
    const newPeriod = event.target.value;
    console.debug(`[Account] Period changed to: ${newPeriod}`);
    setPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      setShowDatePicker(false);
    } else {
      setShowDatePicker(true);
    }
  }, []);

  const handleLocationChange = useCallback((event) => {
    const newLocation = event.target.value;
    console.debug(`[Account] Location changed to: ${newLocation}`);
    setSelectedLocation(newLocation);
    setActiveLocationTab(0);
  }, []);

  const handleLocationTabChange = useCallback((event, newValue) => {
    setActiveLocationTab(newValue);
    if (locationOptions[newValue]) {
      fetchLocationSummary(locationOptions[newValue].id);
    }
  }, [locationOptions, fetchLocationSummary]);

  const handleExport = useCallback(async () => {
    try {
      console.debug('[Account] Exporting data...');
      
      const params = {
        period: period,
        format: 'csv',
      };
      
      if (selectedLocation !== 'all') {
        params.location_id = parseInt(selectedLocation);
      }
      
      if (period === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }
      
      const response = await apiRequest('/account/export', { params });
      
      if (response && response.csv) {
        const blob = new Blob([response.csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.filename || `account_dashboard_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        setSnackbar({
          open: true,
          message: 'Data exported successfully!',
          severity: 'success',
        });
      }
    } catch (err) {
      console.error('[Account] Error exporting data:', err);
      setSnackbar({
        open: true,
        message: `Failed to export data: ${err.message}`,
        severity: 'error',
      });
    }
  }, [period, selectedLocation, customStartDate, customEndDate]);

  const handleCloseLocationSummary = useCallback(() => {
    setLocationSummaryDialogOpen(false);
    setLocationSummary(null);
  }, []);

  const handleApplyCustomDate = useCallback(() => {
    if (customStartDate && customEndDate) {
      console.debug(`[Account] Applying custom date range: ${customStartDate} to ${customEndDate}`);
      // Data will auto-load via useEffect
      setShowDatePicker(false);
    } else {
      setSnackbar({
        open: true,
        message: 'Please select both start and end dates',
        severity: 'warning',
      });
    }
  }, [customStartDate, customEndDate]);

  // ============================================
  // CHART CONFIGURATIONS (memoized)
  // ============================================
  
  const revenueChartData = useMemo(() => {
    if (!dashboardData || !dashboardData.revenue_trend) {
      return { labels: [], datasets: [] };
    }
    
    const trend = dashboardData.revenue_trend;
    const labels = trend.map(d => formatDate(d.date));
    const revenueData = trend.map(d => d.revenue);
    const orderData = trend.map(d => d.orders);
    
    return {
      labels,
      datasets: [
        {
          label: 'Revenue',
          data: revenueData,
          borderColor: '#2E7D32',
          backgroundColor: alpha('#2E7D32', 0.1),
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: '#2E7D32',
          yAxisID: 'y',
        },
        {
          label: 'Orders',
          data: orderData,
          borderColor: '#1976D2',
          backgroundColor: alpha('#1976D2', 0.1),
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: '#1976D2',
          yAxisID: 'y1',
        },
      ],
    };
  }, [dashboardData]);

  const paymentChartData = useMemo(() => {
    if (!dashboardData || !dashboardData.summary?.payment_method_breakdown) {
      return { labels: [], datasets: [] };
    }
    
    const breakdown = dashboardData.summary.payment_method_breakdown;
    const labels = Object.keys(breakdown);
    const data = Object.values(breakdown);
    const colors = labels.map(label => PAYMENT_COLORS[label] || '#9E9E9E');
    
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          borderColor: '#ffffff',
          borderWidth: 3,
        },
      ],
    };
  }, [dashboardData]);

  const locationChartData = useMemo(() => {
    if (!dashboardData || !dashboardData.location_performance) {
      return { labels: [], datasets: [] };
    }
    
    const locations = dashboardData.location_performance;
    const labels = locations.map(l => l.location_name);
    const revenueData = locations.map(l => l.total_revenue);
    
    return {
      labels,
      datasets: [
        {
          label: 'Revenue',
          data: revenueData,
          backgroundColor: locations.map((_, i) => 
            alpha(CHART_COLORS[i % CHART_COLORS.length], 0.7)
          ),
          borderColor: locations.map((_, i) => 
            CHART_COLORS[i % CHART_COLORS.length]
          ),
          borderWidth: 2,
        },
      ],
    };
  }, [dashboardData]);

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  
  const renderOrderClassification = useCallback(() => {
    if (!dashboardData || !dashboardData.summary?.order_classification) {
      return null;
    }
    
    const oc = dashboardData.summary.order_classification;
    
    const classificationData = [
      { label: 'Paid Orders', value: oc.paid_orders, color: '#2E7D32', icon: <PaidIcon /> },
      { label: 'Complimentary', value: oc.complimentary_orders, color: '#FF9800', icon: <ComplimentaryIcon /> },
      { label: 'Canceled', value: oc.canceled_orders, color: '#F44336', icon: <CancelIcon /> },
      { label: 'Discounted', value: oc.discounted_orders, color: '#9C27B0', icon: <DiscountIcon /> },
      { label: 'Unpaid', value: oc.unpaid_orders, color: '#FF5722', icon: <WarningAmberIcon /> },
    ];
    
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', mb: 3 }}>
        <Typography variant="h6" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' } }}>
          📋 Order Classification
        </Typography>
        <Grid container spacing={2}>
          {classificationData.map((item) => (
            <Grid item xs={6} sm={4} md={2.4} key={item.label}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  textAlign: 'center',
                  border: `1px solid ${alpha(item.color, 0.15)}`,
                  bgcolor: alpha(item.color, 0.04),
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: alpha(item.color, 0.1),
                    color: item.color,
                    width: 44,
                    height: 44,
                    mx: 'auto',
                    mb: 1,
                  }}
                >
                  {item.icon}
                </Avatar>
                <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' }, color: item.color }}>
                  {item.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' }, fontWeight: 600 }}>
                  {item.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }, [dashboardData]);

  const renderSummaryCards = useCallback(() => {
    if (!dashboardData) return null;
    
    const { summary } = dashboardData;
    const { previous_period_comparison } = summary;
    
    const cards = [
      {
        title: 'Total Revenue',
        value: formatCurrency(summary.total_revenue),
        subtitle: `${summary.period_days} days • ${summary.total_transactions} transactions`,
        icon: <MoneyIcon />,
        color: '#2E7D32',
        trend: previous_period_comparison?.revenue_change >= 0 ? 'up' : 'down',
        trendValue: Math.abs(previous_period_comparison?.revenue_change || 0).toFixed(1),
      },
      {
        title: 'Total Orders',
        value: summary.total_orders,
        subtitle: `${summary.unique_customers} unique customers`,
        icon: <ReceiptIcon />,
        color: '#1976D2',
        trend: previous_period_comparison?.orders_change >= 0 ? 'up' : 'down',
        trendValue: Math.abs(previous_period_comparison?.orders_change || 0).toFixed(1),
      },
      {
        title: 'Average Order Value',
        value: formatCurrency(summary.average_order_value),
        subtitle: 'Per transaction',
        icon: <TrendingUpIcon />,
        color: '#E65100',
        trend: previous_period_comparison?.avg_order_change >= 0 ? 'up' : 'down',
        trendValue: Math.abs(previous_period_comparison?.avg_order_change || 0).toFixed(1),
      },
      {
        title: 'Total Discounts',
        value: formatCurrency(summary.total_discounts),
        subtitle: summary.total_complimentary > 0 ? `${formatCurrency(summary.total_complimentary)} complimentary` : 'No complimentary orders',
        icon: <ComplimentaryIcon />,
        color: '#FF9800',
        trend: null,
      },
    ];
    
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatCard {...card} loading={isFetching} />
          </Grid>
        ))}
      </Grid>
    );
  }, [dashboardData, isFetching]);

  const renderAlerts = useCallback(() => {
    if (!dashboardData || !dashboardData.alerts || dashboardData.alerts.length === 0) {
      return null;
    }
    
    const severityMap = {
      high: { severity: 'error', icon: <ErrorIcon /> },
      medium: { severity: 'warning', icon: <WarningAmberIcon /> },
      low: { severity: 'info', icon: <InfoIcon /> },
    };
    
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' } }}>
          ⚠️ Alerts & Notifications
        </Typography>
        <Stack spacing={1.5}>
          {dashboardData.alerts.map((alert, index) => {
            const { severity, icon } = severityMap[alert.severity] || severityMap.low;
            return (
              <Alert
                key={index}
                severity={severity}
                icon={icon}
                variant="outlined"
                sx={{
                  borderRadius: '12px',
                  '& .MuiAlert-message': {
                    width: '100%',
                  },
                }}
              >
                <Typography variant="subtitle2" fontWeight={800} sx={{ fontSize: { xs: '0.85rem', sm: '1rem', md: '1.05rem' } }}>
                  {alert.title}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' } }}>
                  {alert.message}
                </Typography>
              </Alert>
            );
          })}
        </Stack>
      </Box>
    );
  }, [dashboardData]);

  const renderPaymentMethodBreakdown = useCallback(() => {
    if (!dashboardData || !dashboardData.summary?.payment_method_breakdown) {
      return (
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', height: '100%' }}>
          <Typography variant="h6" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' } }}>
            Payment Methods
          </Typography>
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No payment data available
          </Typography>
        </Paper>
      );
    }
    
    const breakdown = dashboardData.summary.payment_method_breakdown;
    const total = Object.values(breakdown).reduce((a, b) => a + b, 0);
    
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', height: '100%' }}>
        <Typography variant="h6" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' } }}>
          💳 Payment Methods
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ height: { xs: 200, sm: 240, md: 260 }, position: 'relative' }}>
              <Doughnut
                data={paymentChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  cutout: '60%',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  Total
                </Typography>
                <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' } }}>
                  {formatCurrency(total)}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={1.5}>
              {Object.entries(breakdown).map(([method, amount]) => {
                const percentage = total > 0 ? (amount / total) * 100 : 0;
                const color = PAYMENT_COLORS[method] || '#9E9E9E';
                
                return (
                  <Box key={method}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' } }}>
                          {method}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' } }}>
                        {formatCurrency(amount)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(color, 0.15),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: color,
                          borderRadius: 4,
                        },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                      {percentage.toFixed(1)}%
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    );
  }, [dashboardData, paymentChartData]);

  const renderRevenueTrend = useCallback(() => {
    if (!dashboardData || !dashboardData.revenue_trend || dashboardData.revenue_trend.length === 0) {
      return (
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', height: '100%' }}>
          <Typography variant="h6" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' } }}>
            Revenue Trend
          </Typography>
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No trend data available
          </Typography>
        </Paper>
      );
    }
    
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', height: '100%' }}>
        <Typography variant="h6" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' } }}>
          📈 Revenue & Orders Trend
        </Typography>
        <Box sx={{ height: { xs: 220, sm: 260, md: 300 } }}>
          <Line
            data={revenueChartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: {
                mode: 'index',
                intersect: false,
              },
              plugins: {
                legend: {
                  position: 'top',
                  labels: {
                    font: { size: 12, weight: 'bold' },
                    boxWidth: 14,
                    padding: 12,
                  },
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  position: 'left',
                  grid: { color: alpha('#000', 0.06) },
                  ticks: {
                    font: { size: 11, weight: 'bold' },
                    callback: (value) => formatCurrency(value, '₦'),
                  },
                },
                y1: {
                  beginAtZero: true,
                  position: 'right',
                  grid: { display: false },
                  ticks: {
                    font: { size: 11, weight: 'bold' },
                  },
                },
                x: {
                  grid: { display: false },
                  ticks: {
                    font: { size: 11, weight: 'bold' },
                    maxRotation: 45,
                  },
                },
              },
            }}
          />
        </Box>
      </Paper>
    );
  }, [dashboardData, revenueChartData]);

  const renderLocationPerformance = useCallback(() => {
    if (!dashboardData || !dashboardData.location_performance || dashboardData.location_performance.length === 0) {
      return (
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px' }}>
          <Typography variant="h6" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' } }}>
            Location Performance
          </Typography>
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No location data available
          </Typography>
        </Paper>
      );
    }
    
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px' }}>
        <Typography variant="h6" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' } }}>
          🏢 Location Performance
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Box sx={{ height: { xs: 220, sm: 260, md: 300 } }}>
              <Bar
                data={locationChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: { color: alpha('#000', 0.06) },
                      ticks: {
                        font: { size: 11, weight: 'bold' },
                        callback: (value) => formatCurrency(value, '₦'),
                      },
                    },
                    x: {
                      grid: { display: false },
                      ticks: {
                        font: { size: 11, weight: 'bold' },
                      },
                    },
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Stack spacing={2}>
              {dashboardData.location_performance.map((loc) => {
                const color = getLocationColor(loc.location_id);
                const growthColor = loc.growth_percentage >= 0 ? '#4CAF50' : '#F44336';
                const GrowthIcon = loc.growth_percentage >= 0 ? ArrowUpIcon : ArrowDownIcon;
                
                return (
                  <Paper
                    key={loc.location_id}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      border: `1px solid ${alpha(color.primary, 0.15)}`,
                      bgcolor: alpha(color.primary, 0.04),
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateX(4px)',
                        borderColor: color.primary,
                      },
                    }}
                    onClick={() => {
                      setSelectedLocation(String(loc.location_id));
                      fetchLocationSummary(loc.location_id);
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(color.primary, 0.12),
                            color: color.primary,
                            width: 40,
                            height: 40,
                          }}
                        >
                          <LocationIcon sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={800} sx={{ fontSize: { xs: '0.85rem', sm: '1rem', md: '1.05rem' } }}>
                            {loc.location_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                            {loc.total_orders} orders
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight={800} sx={{ fontSize: { xs: '0.85rem', sm: '1rem', md: '1.05rem' } }}>
                          {formatCurrency(loc.total_revenue)}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${loc.growth_percentage >= 0 ? '+' : ''}${loc.growth_percentage.toFixed(1)}%`}
                          sx={{
                            height: 24,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: alpha(growthColor, 0.1),
                            color: growthColor,
                          }}
                          icon={<GrowthIcon sx={{ fontSize: 14 }} />}
                        />
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    );
  }, [dashboardData, locationChartData, fetchLocationSummary]);

  const renderTopItems = useCallback(() => {
    if (!dashboardData || !dashboardData.top_items || dashboardData.top_items.length === 0) {
      return (
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', height: '100%' }}>
          <Typography variant="h6" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' } }}>
            Top Items
          </Typography>
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No item data available
          </Typography>
        </Paper>
      );
    }
    
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', height: '100%' }}>
        <Typography variant="h6" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' } }}>
          🏆 Top Selling Items
        </Typography>
        <Stack spacing={1.5}>
          {dashboardData.top_items.slice(0, 5).map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
                borderRadius: '10px',
                bgcolor: index === 0 ? alpha('#FFD700', 0.08) : 'transparent',
                border: index === 0 ? `1px solid ${alpha('#FFD700', 0.3)}` : 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: alpha('#000', 0.02),
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: index === 0 ? '#FFD700' : alpha(CHART_COLORS[index % CHART_COLORS.length], 0.1),
                    color: index === 0 ? '#000' : CHART_COLORS[index % CHART_COLORS.length],
                    fontSize: '0.8rem',
                    fontWeight: 800,
                  }}
                >
                  {index + 1}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={800}
                    sx={{
                      fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.item_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                    {item.category} • {item.total_quantity} sold
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right', ml: 1 }}>
                <Typography variant="body2" fontWeight={800} sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' } }}>
                  {formatCurrency(item.total_revenue)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                  {item.order_count} orders
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </Paper>
    );
  }, [dashboardData]);

  const renderTopWaiters = useCallback(() => {
    if (!dashboardData || !dashboardData.top_waiters || dashboardData.top_waiters.length === 0) {
      return (
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', height: '100%' }}>
          <Typography variant="h6" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' } }}>
            Top Waiters
          </Typography>
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No waiter data available
          </Typography>
        </Paper>
      );
    }
    
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', height: '100%' }}>
        <Typography variant="h6" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' } }}>
          👨‍🍳 Top Performing Waiters
        </Typography>
        <Stack spacing={1.5}>
          {dashboardData.top_waiters.slice(0, 5).map((waiter, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
                borderRadius: '10px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: alpha('#000', 0.02),
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: alpha('#1976D2', 0.1),
                    color: '#1976D2',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                  }}
                >
                  {getInitials(waiter.waiter_name)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={800}
                    sx={{
                      fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {waiter.waiter_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                    {waiter.order_count} orders • Avg {formatCurrency(waiter.average_order_value)}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" fontWeight={800} sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' }, color: '#1976D2' }}>
                {formatCurrency(waiter.total_revenue)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
    );
  }, [dashboardData]);

  const renderTransactions = useCallback(() => {
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' } }}>
            📋 Recent Transactions
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={`${transactionsTotal} transactions`}
              color="primary"
              variant="outlined"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' }, fontWeight: 700 }}
            />
            {transactions.length > 0 && (
              <Chip
                size="small"
                label={`Revenue: ${formatCurrency(transactions.reduce((sum, t) => sum + (t.is_canceled || t.is_complimentary ? 0 : t.amount), 0))}`}
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' },
                  fontWeight: 700,
                  bgcolor: alpha('#2E7D32', 0.1),
                  color: '#2E7D32',
                }}
              />
            )}
          </Box>
        </Box>
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha('#000', 0.02) }}>
                <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 800 }}>Order #</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 800 }}>Date</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 800 }}>Location</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 800 }}>Customer</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 800 }}>Payment</TableCell>
                <TableCell align="right" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 800 }}>Amount</TableCell>
                <TableCell align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 800 }}>Status</TableCell>
                <TableCell align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 800 }}>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' }, fontWeight: 600 }}>
                      No transactions found for this period
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((tx) => {
                  let statusColor = '#2E7D32';
                  let statusLabel = tx.status;
                  let typeLabel = 'Regular';
                  let typeColor = '#1976D2';
                  
                  if (tx.is_canceled) {
                    statusColor = '#F44336';
                    typeLabel = 'Canceled';
                    typeColor = '#F44336';
                  } else if (tx.is_complimentary) {
                    statusColor = '#FF9800';
                    typeLabel = 'Complimentary';
                    typeColor = '#FF9800';
                  } else if (tx.discount_amount > 0) {
                    typeLabel = 'Discounted';
                    typeColor = '#9C27B0';
                  }
                  
                  return (
                    <TableRow key={tx.id} hover>
                      <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem', md: '0.85rem' }, fontWeight: 700 }}>
                        {tx.order_number || `#${tx.id}`}
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem', md: '0.8rem' } }}>
                        {formatDateTime(tx.date)}
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem', md: '0.8rem' } }}>
                        {tx.location}
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem', md: '0.8rem' } }}>
                        {tx.customer_name || 'Walk-in'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={tx.payment_method || 'Unpaid'}
                          sx={{
                            fontSize: { xs: '0.55rem', sm: '0.65rem', md: '0.7rem' },
                            fontWeight: 700,
                            height: { xs: 22, sm: 26, md: 28 },
                            bgcolor: alpha(PAYMENT_COLORS[tx.payment_method] || '#9E9E9E', 0.1),
                            color: PAYMENT_COLORS[tx.payment_method] || '#9E9E9E',
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem', md: '0.85rem' }, fontWeight: 700 }}>
                        {tx.is_canceled || tx.is_complimentary ? '-' : formatCurrency(tx.amount)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={statusLabel}
                          sx={{
                            fontSize: { xs: '0.55rem', sm: '0.65rem', md: '0.7rem' },
                            fontWeight: 700,
                            height: { xs: 22, sm: 26, md: 28 },
                            bgcolor: alpha(statusColor, 0.1),
                            color: statusColor,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={typeLabel}
                          sx={{
                            fontSize: { xs: '0.55rem', sm: '0.65rem', md: '0.7rem' },
                            fontWeight: 700,
                            height: { xs: 22, sm: 26, md: 28 },
                            bgcolor: alpha(typeColor, 0.1),
                            color: typeColor,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {transactionsTotal > transactionPageSize && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              size="small"
              variant="outlined"
              disabled={transactionPage <= 1}
              onClick={() => setTransactionPage(p => p - 1)}
              sx={{ mr: 1, fontWeight: 700 }}
            >
              Previous
            </Button>
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', px: 2, fontWeight: 700 }}>
              Page {transactionPage} of {Math.ceil(transactionsTotal / transactionPageSize)}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={transactions.length < transactionPageSize}
              onClick={() => setTransactionPage(p => p + 1)}
              sx={{ ml: 1, fontWeight: 700 }}
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>
    );
  }, [transactions, transactionsTotal, transactionPage, transactionPageSize]);

  // ============================================
  // LOCATION SUMMARY DIALOG
  // ============================================
  
  const renderLocationSummaryDialog = useCallback(() => {
    if (!locationSummary) return null;
    
    const { summary, order_status_breakdown, payment_status_breakdown, daily_breakdown } = locationSummary;
    
    return (
      <Dialog
        open={locationSummaryDialogOpen}
        onClose={handleCloseLocationSummary}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: alpha('#1976D2', 0.04),
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon sx={{ color: '#1976D2', fontSize: 30 }} />
            <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' } }}>
              {locationSummary.location_name} - Detailed Summary
            </Typography>
          </Box>
          <IconButton onClick={handleCloseLocationSummary} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4}>
              <Paper sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center', borderRadius: '12px' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' }, fontWeight: 600 }}>
                  Revenue
                </Typography>
                <Typography variant="h6" fontWeight={800} color="#2E7D32" sx={{ fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' } }}>
                  {formatCurrency(summary.total_revenue)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Paper sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center', borderRadius: '12px' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' }, fontWeight: 600 }}>
                  Orders
                </Typography>
                <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' } }}>
                  {summary.total_orders}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Paper sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center', borderRadius: '12px' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' }, fontWeight: 600 }}>
                  Avg Order
                </Typography>
                <Typography variant="h6" fontWeight={800} color="#E65100" sx={{ fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' } }}>
                  {formatCurrency(summary.average_order_value)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: '12px' }}>
                <Typography variant="subtitle2" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' } }}>
                  Order Status
                </Typography>
                {Object.entries(order_status_breakdown || {}).map(([status, count]) => (
                  <Box key={status} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' }, fontWeight: 600 }}>
                      {status}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' } }}>
                      {count}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: '12px' }}>
                <Typography variant="subtitle2" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' } }}>
                  Payment Status
                </Typography>
                {Object.entries(payment_status_breakdown || {}).map(([status, count]) => (
                  <Box key={status} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' }, fontWeight: 600 }}>
                      {status}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' } }}>
                      {count}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Grid>
          </Grid>
          
          {daily_breakdown && daily_breakdown.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' } }}>
                Daily Performance
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px', maxHeight: 200 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.75rem' }, fontWeight: 700 }}>Date</TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.75rem' }, fontWeight: 700 }}>Revenue</TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.75rem' }, fontWeight: 700 }}>Orders</TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.75rem' }, fontWeight: 700 }}>Avg</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {daily_breakdown.map((day) => (
                      <TableRow key={day.date}>
                        <TableCell sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.75rem' } }}>{formatDate(day.date)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.75rem' }, fontWeight: 700 }}>
                          {formatCurrency(day.revenue)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.75rem' } }}>{day.orders}</TableCell>
                        <TableCell align="right" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.75rem' }, fontWeight: 700 }}>
                          {formatCurrency(day.avg_order)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: 2,
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
        }}>
          <Button
            onClick={handleCloseLocationSummary}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: '8px',
              px: 3,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }, [locationSummary, locationSummaryDialogOpen, handleCloseLocationSummary]);

  // ============================================
  // LOADING STATE
  // ============================================
  
  if (isLoading) {
    return <Preloader text="Loading Account Dashboard..." size={70} fullScreen />;
  }

  // ============================================
  // MAIN RENDER - Using epos-wrapper pattern
  // ============================================

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <AccountSidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
        
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}>
          <AccountHeader />
          
          <Box sx={{
            flex: 1,
            overflow: 'auto',
            p: { xs: 1.5, sm: 2, md: 3 },
            bgcolor: '#f8fafc',
            '&::-webkit-scrollbar': {
              width: 6,
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'grey.300',
              borderRadius: 3,
            },
          }}>
            {/* Header Section */}
            <Box sx={{
              bgcolor: 'white',
              borderRadius: '16px',
              p: { xs: 1.5, sm: 2.5, md: 3 },
              mb: { xs: 1.5, sm: 2.5, md: 3 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexWrap: 'wrap',
              gap: 2,
              border: `1px solid ${alpha('#1976D2', 0.12)}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
              <Box>
                <Typography variant="h4" sx={{
                  color: '#1976D2',
                  fontWeight: 800,
                  fontSize: { xs: '1.4rem', sm: '2rem', md: '2.4rem' },
                }}>
                  📊 Account Dashboard
                  <span style={{
                    display: 'inline-block',
                    width: '4px',
                    height: { xs: '28px', sm: '36px', md: '40px' },
                    backgroundColor: '#1976D2',
                    marginLeft: '8px',
                    verticalAlign: 'text-bottom',
                    animation: 'blink 1s step-end infinite',
                  }} />
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }, fontWeight: 600 }}>
                  {dashboardData?.summary?.period_days || 0} days • {formatDate(new Date())}
                </Typography>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 1, sm: 1.5 }, 
                flexWrap: 'wrap',
                width: { xs: '100%', sm: 'auto' },
              }}>
                <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 140, md: 160 } }}>
                  <Select
                    value={period}
                    onChange={handlePeriodChange}
                    displayEmpty
                    sx={{
                      borderRadius: '10px',
                      fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                      fontWeight: 700,
                      '& .MuiSelect-select': {
                        py: { xs: 0.75, sm: 1 },
                      },
                    }}
                  >
                    {PERIOD_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 700 }}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {period === 'custom' && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${alpha('#000', 0.15)}`,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        background: 'white',
                        minWidth: '120px',
                      }}
                    />
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#666' }}>to</span>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${alpha('#000', 0.15)}`,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        background: 'white',
                        minWidth: '120px',
                      }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleApplyCustomDate}
                      sx={{
                        bgcolor: '#1976D2',
                        '&:hover': { bgcolor: '#1565C0' },
                        textTransform: 'none',
                        fontWeight: 800,
                        fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' },
                        borderRadius: '8px',
                        px: 2,
                      }}
                    >
                      Apply
                    </Button>
                  </Box>
                )}
                
                <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 140, md: 160 } }}>
                  <Select
                    value={selectedLocation}
                    onChange={handleLocationChange}
                    displayEmpty
                    sx={{
                      borderRadius: '10px',
                      fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                      fontWeight: 700,
                      '& .MuiSelect-select': {
                        py: { xs: 0.75, sm: 1 },
                      },
                    }}
                  >
                    <MenuItem value="all" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 700 }}>All Locations</MenuItem>
                    <MenuItem value="3" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 700 }}>Continental</MenuItem>
                    <MenuItem value="4" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 700 }}>Lounge</MenuItem>
                    <MenuItem value="5" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 700 }}>African</MenuItem>
                  </Select>
                </FormControl>
                
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExport}
                  startIcon={<DownloadIcon />}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' },
                    borderColor: '#1976D2',
                    color: '#1976D2',
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.5, sm: 0.75 },
                    flex: { xs: 1, sm: 'none' },
                    '&:hover': {
                      borderColor: '#1976D2',
                      bgcolor: alpha('#1976D2', 0.08),
                    },
                  }}
                >
                  Export
                </Button>
                
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleRefresh}
                  startIcon={isFetching ? <CircularProgress size={22} color="inherit" /> : <RefreshIcon />}
                  sx={{
                    bgcolor: '#1976D2',
                    '&:hover': { bgcolor: '#1565C0' },
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' },
                    borderRadius: '10px',
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.5, sm: 0.75 },
                    flex: { xs: 1, sm: 'none' },
                  }}
                  disabled={isFetching}
                >
                  {isFetching ? 'Loading...' : 'Refresh'}
                </Button>
              </Box>
            </Box>

            {/* Order Classification */}
            {renderOrderClassification()}

            {/* Alerts */}
            {renderAlerts()}

            {/* Summary Cards */}
            {renderSummaryCards()}

            {/* Charts Row */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                {renderPaymentMethodBreakdown()}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderRevenueTrend()}
              </Grid>
            </Grid>

            {/* Location Performance */}
            <Box sx={{ mb: 3 }}>
              {renderLocationPerformance()}
            </Box>

            {/* Top Items and Waiters */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                {renderTopItems()}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderTopWaiters()}
              </Grid>
            </Grid>

            {/* Transactions */}
            {renderTransactions()}
          </Box>
        </Box>
      </Box>
      <AccountFooter />
      
      {/* Location Summary Dialog */}
      {renderLocationSummaryDialog()}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
        `}
      </style>
    </Box>
  );
};

export default AccountDashboard;